import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { api } from "../lib/api";
import {
  LayoutDashboard,
  Package,
  Car,
  LogOut,
  AlertTriangle,
  Users,
  Wrench,
  Clock,
  ClipboardList,
  UserCog,
  Menu,
  Contact,
} from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_fleet-manager-482/artifacts/fm874382_Image.jpeg";

function SidebarContent({ navItems, user, role, onLogout, onNavigate, unread }) {
  return (
    <>
      <div className="px-6 py-6 border-b border-zinc-200 flex items-center justify-center">
        <img src={LOGO_URL} alt="JDMDRE" className="h-10 w-auto object-contain" data-testid="sidebar-logo" />
      </div>
      <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            data-testid={item.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-sm transition-colors duration-150 ${
                isActive
                  ? "bg-zinc-950 text-white"
                  : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-950"
              }`
            }
          >
            <item.icon className="w-4 h-4" strokeWidth={2} />
            <span className="flex-1">{item.label}</span>
            {item.to === "/crm" && unread > 0 && (
              <span data-testid="crm-unread-badge" className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold tracking-wide bg-[#FF2400] text-white rounded-sm">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-zinc-200">
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">Signed in as</div>
          <div className="text-sm text-zinc-900 truncate" data-testid="user-email">
            {user?.name || user?.phone}
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mt-0.5 font-mono">
            {user?.phone} · {role}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onLogout}
          data-testid="logout-btn"
          className="w-full rounded-sm border-zinc-300"
        >
          <LogOut className="w-4 h-4 mr-2" /> Log out
        </Button>
      </div>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const role = user?.role;
  const isAdmin = role === "admin";
  const isFull = role === "admin" || role === "full";

  // Poll CRM unread count (admin only)
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/crm/unread-count");
        if (!cancelled) setUnread(data?.count || 0);
      } catch (_) {
        /* ignore */
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000); // 30s
    return () => { cancelled = true; clearInterval(id); };
  }, [isAdmin, location.pathname]);

  const navItems = isFull
    ? [
        { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
        { to: "/parts", label: "Inventory", icon: Package, testid: "nav-parts" },
        { to: "/vehicles", label: "Vehicles", icon: Car, testid: "nav-vehicles" },
        { to: "/services", label: settings.services_label, icon: Wrench, testid: "nav-services" },
        { to: "/suppliers", label: "Suppliers", icon: Users, testid: "nav-suppliers" },
        { to: "/alerts", label: "Reorder Alerts", icon: AlertTriangle, testid: "nav-alerts" },
        ...(isAdmin ? [] : [{ to: "/my-timecard", label: "My Timecard", icon: Clock, testid: "nav-my-timecard" }]),
        ...(isAdmin ? [{ to: "/timecards", label: "Timecards", icon: Clock, testid: "nav-timecards" }] : []),
        { to: "/supply-requests", label: "Supply Requests", icon: ClipboardList, testid: "nav-supply" },
        ...((isAdmin || user?.crm_access) ? [{ to: "/crm", label: "CRM", icon: Contact, testid: "nav-crm" }] : []),
        ...(isAdmin ? [{ to: "/users", label: "Users", icon: UserCog, testid: "nav-users" }] : []),
      ]
    : [
        { to: "/my-timecard", label: "My Timecard", icon: Clock, testid: "nav-my-timecard" },
        { to: "/supply-requests", label: "Supply Requests", icon: ClipboardList, testid: "nav-supply" },
        ...(user?.crm_access ? [{ to: "/crm", label: "CRM", icon: Contact, testid: "nav-crm" }] : []),
      ];

  const currentLabel = navItems.find((i) => i.to === location.pathname || (i.to === "/" && location.pathname === "/"))?.label || "";

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-zinc-100">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex w-64 bg-white border-r border-zinc-200 flex-col sticky top-0 h-screen"
        data-testid="sidebar"
      >
        <SidebarContent navItems={navItems} user={user} role={role} onLogout={onLogout} unread={unread} />
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-zinc-200 flex items-center justify-between px-4 py-3">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-btn"
                className="rounded-sm relative"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
                {unread > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-[#FF2400] rounded-full" data-testid="mobile-unread-dot" />
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 flex flex-col" data-testid="mobile-sidebar">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <SidebarContent
                navItems={navItems}
                user={user}
                role={role}
                onLogout={async () => { await onLogout(); setMobileOpen(false); }}
                onNavigate={() => setMobileOpen(false)}
                unread={unread}
              />
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-3 flex-1 justify-center">
            <img src={LOGO_URL} alt="JDMDRE" className="h-7 w-auto object-contain" />
          </div>
          <div className="w-10" aria-hidden />
        </header>

        <main className="flex-1 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
